import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, keys } from "@shared/api";
import type { Channel, Named, Role, Uuid } from "@shared/api";
import { Markup } from "@shared/ui/Markup";
import { dayTime } from "@shared/lib/format";
import type { CustomEmoji } from "@shared/lib/emoji";
import type { MessageLink } from "@shared/lib/jump";
import { IconClose } from "@shared/ui/icons";
import { fromSelf, livePerson, Tile } from "@entities/user";
import { useMe } from "@entities/session";
import { useStore } from "@features/chat";
import { MessageExtras } from "./MessageRow";

/**
 * Закреплённые в канале — по образцу Discord: последние закреплённые сверху,
 * у каждого — переход к самому сообщению в ленте и, кому можно, «открепить».
 * Сообщение показано целиком, как в ленте: опрос, превью ссылок, голосовое,
 * файлы и пересланное — тем же `MessageExtras`, а не «1 вложение».
 *
 * Список берётся отдельным запросом, а не из ленты: закреплённое обычно старое
 * и в окно ленты не попадает. Свежим его держит событие `pin`.
 */
export function PinnedList({
  channelId,
  threadId = null,
  canPin,
  people,
  custom,
  channels,
  roles,
  onOpenChannel,
  onOpenProfile,
  onOpenMessage,
  onOpenServer,
}: {
  channelId: Uuid;
  /** Закреплённые ветки — у неё список свой, в закреплённых канала её ответов нет. */
  threadId?: Uuid | null;
  canPin: boolean;
  people: Map<Uuid, Named>;
  custom: CustomEmoji[];
  channels: Channel[];
  roles: Role[];
  onOpenChannel: (channelId: Uuid) => void;
  onOpenProfile: (userId: Uuid, anchor?: HTMLElement) => void;
  /** Ссылка на сообщение в тексте и «откуда переслано». */
  onOpenMessage?: (link: MessageLink) => void;
  /** Открыть сервер после вступления по карточке приглашения. */
  onOpenServer?: (serverId: Uuid, channelId: Uuid | null) => void;
}) {
  const store = useStore();
  const queryClient = useQueryClient();
  const self = useMe();
  const me = fromSelf(self);
  const { data: pins, isPending } = useQuery({
    queryKey: keys.pins(threadId ?? channelId),
    queryFn: () => (threadId ? api.messages.threadPins(channelId, threadId) : api.messages.pins(channelId)),
  });

  const nameOf = useMemo(() => {
    const byHandle = new Map([...people.values()].map((person) => [person.handle, person]));
    return (handle: string) => {
      const person = byHandle.get(handle);
      return person ? person.display_name || person.username : null;
    };
  }, [people]);
  const channelOf = useMemo(() => {
    const byId = new Map(channels.map((item) => [item.id, item.name]));
    return (id: string) => byId.get(id) ?? null;
  }, [channels]);
  const roleOf = useMemo(() => {
    const byId = new Map(roles.map((item) => [item.id, item.name]));
    return (id: string) => byId.get(id) ?? null;
  }, [roles]);
  const emojiOf = useMemo(() => {
    const byName = new Map(custom.map((item) => [item.name, item]));
    return (name: string) => byName.get(name) ?? null;
  }, [custom]);

  // Голос и конец опроса стор кладёт в ленту, а список здесь — свой запрос.
  const refresh = () => queryClient.invalidateQueries({ queryKey: keys.pins(threadId ?? channelId) });

  if (isPending) return null;

  if (!pins?.length) {
    return (
      <div className="side-col-note">
        Здесь пока ничего не закреплено.
        {canPin && (
          <p style={{ margin: "8px 0 0" }}>
            Важное держат на виду: правой кнопкой по сообщению — «Закрепить».
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="pin-list">
      {pins.map((message) => {
        const author = livePerson(message.author, people, me);
        return (
          <article key={message.id} className="pin-card">
            <header className="pin-head">
              <button type="button" className="pin-who" onClick={(event) => onOpenProfile(message.author.id, event.currentTarget)}>
                <Tile person={author} size={22} mark={null} />
                <span className="pin-name ellipsis">{author.name}</span>
              </button>
              {canPin && (
                <button
                  type="button"
                  className="icon-btn pin-off"
                  title="Открепить"
                  onClick={() => void store.setPinned(channelId, message.id, false)}
                >
                  <IconClose size={13} />
                </button>
              )}
            </header>

            {message.content && (
              <div className="pin-text">
                <Markup
                  text={message.content}
                  nameOf={nameOf}
                  mine={self.handle}
                  emojiOf={emojiOf}
                  channelOf={channelOf}
                  roleOf={roleOf}
                  onOpenChannel={onOpenChannel}
                  onOpenMessage={onOpenMessage}
                />
              </div>
            )}
            <MessageExtras
              message={message}
              people={people}
              nameOf={nameOf}
              mine={self.handle}
              emojiOf={emojiOf}
              channelOf={channelOf}
              roleOf={roleOf}
              onOpenChannel={onOpenChannel}
              onOpenMessage={onOpenMessage}
              onOpenServer={onOpenServer}
              onVote={message.poll ? (ids) => store.vote(channelId, message.id, ids).then(refresh) : undefined}
              onEndPoll={
                message.poll && message.author.id === self.id
                  ? () => store.endPoll(channelId, message.id).then(refresh)
                  : undefined
              }
            />

            <footer className="pin-foot">
              <span className="pin-at">{dayTime(message.created)}</span>
              <button type="button" className="pin-go" onClick={() => void store.openAt(channelId, message.id)}>
                Перейти
              </button>
            </footer>
          </article>
        );
      })}
    </div>
  );
}
